import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import type { Brand } from '@/components/finance/BrandSelector';

export interface PeriodReceivablesSummary {
  periodo: string;
  periodo_label: string;
  total_aberto: number;
  total_atrasado: number;
  quantidade_pagamentos: number;
  quantidade_clientes: number;
  business_unit?: string;
}

// Keep old interface for backwards compatibility
export type WeeklyReceivablesSummary = PeriodReceivablesSummary & {
  semana: string;
  semana_label: string;
};

interface UseReceivablesPeriodOptions {
  businessUnit?: BusinessUnit;
  brand?: Brand;
  groupBy?: 'week' | 'month';
}

export function useReceivablesPeriod(options: UseReceivablesPeriodOptions = {}) {
  const { 
    businessUnit = 'all', 
    brand = 'all',
    groupBy = 'week',
  } = options;

  return useQuery({
    queryKey: ['receivables-period', businessUnit, brand, groupBy],
    queryFn: async () => {
      let query = supabase
        .from('v_payments_aging_detail')
        .select('due_date, valor_aberto, dias_atraso, contact_id, business_unit, brand')
        .in('status', ['pending', 'overdue'])
        .not('due_date', 'is', null);

      if (businessUnit !== 'all') {
        query = query.eq('business_unit', businessUnit);
      }
      
      if (brand !== 'all') {
        query = query.eq('brand', brand);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      const periodMap = new Map<string, PeriodReceivablesSummary>();
      const clientsByPeriod = new Map<string, Set<string>>();
      
      data?.forEach(payment => {
        if (!payment.due_date) return;
        
        const dueDate = new Date(payment.due_date);
        let periodKey: string;
        let periodLabel: string;
        
        if (groupBy === 'month') {
          // Group by month
          const year = dueDate.getFullYear();
          const month = dueDate.getMonth();
          periodKey = `${year}-${String(month + 1).padStart(2, '0')}`;
          periodLabel = formatMonthLabel(year, month);
        } else {
          // Group by week (Monday start)
          const dayOfWeek = dueDate.getDay();
          const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          const weekStart = new Date(dueDate);
          weekStart.setDate(dueDate.getDate() + diff);
          weekStart.setHours(0, 0, 0, 0);
          periodKey = weekStart.toISOString().split('T')[0];
          periodLabel = formatWeekLabel(weekStart);
        }
        
        const existing = periodMap.get(periodKey) || {
          periodo: periodKey,
          periodo_label: periodLabel,
          total_aberto: 0,
          total_atrasado: 0,
          quantidade_pagamentos: 0,
          quantidade_clientes: 0,
        };
        
        existing.total_aberto += payment.valor_aberto || 0;
        existing.quantidade_pagamentos += 1;
        
        if (payment.dias_atraso > 0) {
          existing.total_atrasado += payment.valor_aberto || 0;
        }
        
        periodMap.set(periodKey, existing);
        
        // Track unique clients
        if (payment.contact_id) {
          if (!clientsByPeriod.has(periodKey)) {
            clientsByPeriod.set(periodKey, new Set());
          }
          clientsByPeriod.get(periodKey)!.add(payment.contact_id);
        }
      });
      
      // Update client counts
      clientsByPeriod.forEach((clients, periodKey) => {
        const summary = periodMap.get(periodKey);
        if (summary) {
          summary.quantidade_clientes = clients.size;
        }
      });
      
      // Sort by period and return
      const result = Array.from(periodMap.values()).sort(
        (a, b) => a.periodo.localeCompare(b.periodo)
      );
      
      return result;
    },
  });
}

// Backwards compatible hook
export function useReceivablesWeekly(options: Omit<UseReceivablesPeriodOptions, 'groupBy'> = {}) {
  return useReceivablesPeriod({ ...options, groupBy: 'week' });
}

export function useReceivablesMonthly(options: Omit<UseReceivablesPeriodOptions, 'groupBy'> = {}) {
  return useReceivablesPeriod({ ...options, groupBy: 'month' });
}

function formatWeekLabel(weekStart: Date): string {
  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  
  return `Semana de ${weekStart.getDate()} de ${months[weekStart.getMonth()]}`;
}

function formatMonthLabel(year: number, month: number): string {
  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  return `${months[month]} ${year}`;
}
